import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useState } from "react";
import { ExternalLink, FileText } from "lucide-react";

import { AppShell } from "@/components/app/app-shell";
import { kitsQuery } from "@/lib/queries";

export const Route = createFileRoute("/_authenticated/kits")({
  head: () => ({
    meta: [
      { title: "Enablement Kits | Warrior Volunteer" },
      { name: "description", content: "Enablement Kits in the T1D Warriors volunteer workspace." },
      { property: "og:title", content: "Enablement Kits | Warrior Volunteer" },
      { property: "og:description", content: "Enablement Kits in the T1D Warriors volunteer workspace." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: KitsPage,
});

function KitsPage() {
  const { data: kits = [], isLoading } = useQuery(kitsQuery);
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <AppShell eyebrow="Enablement" title="Enablement kits">
      <section className="panel mb-6 p-6">
        <h2 className="t-h3 text-ice">New here? Start with this</h2>
        <p className="t-body mt-2 text-sm">
          An enablement kit is everything you need to introduce one T1D Warriors program to a
          school, business, church, or family. Each kit holds a ready to send email, a short call
          script with talking tips, text message options, and printable one pagers or flyers. You do
          not have to write anything from scratch.
        </p>
        <ol className="mt-4 space-y-2 text-sm text-ice">
          <li>
            <span className="text-cyan">1.</span> Open the kit for the program you want to pitch and
            read the intro so you know who it is for.
          </li>
          <li>
            <span className="text-cyan">2.</span> Add the person to Contacts, or find them there if
            they already exist. It's quickest to do this from the Inbox.
          </li>
          <li>
            <span className="text-cyan">3.</span> Go to the Inbox and click New message. Choose your
            contact, pick the enablement kit as your template, and send. The email goes out from your
            name and the reply lands back in the Inbox as a thread.
          </li>
          <li>
            <span className="text-cyan">4.</span> Prefer to call or text? Copy the call script or a
            text template from the kit, then log the touch on the contact.
          </li>
          <li>
            <span className="text-cyan">5.</span> Watch the Inbox for the reply and keep the thread
            going right there.
          </li>
        </ol>
        <p className="t-meta mt-4">
          Tip: personalize the first line or two. A short, human note beats a polished pitch every
          time.
        </p>
      </section>

      {isLoading ? <p className="t-body">Loading kits...</p> : null}


      <div className="space-y-4">
        {kits.map((kit) => {
          const open = openId === kit.id;
          return (
            <article key={kit.id} className="panel overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenId(open ? null : kit.id)}
                className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
              >
                <div>
                  <p className="t-eyebrow">{kit.programs?.name}</p>
                  <h2 className="t-h3 mt-1.5 text-ice">{kit.title}</h2>
                </div>
                <span className="t-meta shrink-0">{open ? "Close" : "Open kit"}</span>
              </button>

              {open ? (
                <div className="space-y-6 border-t border-border px-6 py-6">
                  <div>
                    <p className="t-meta">How to use this</p>
                    <p className="t-body mt-2 text-sm">{kit.intro}</p>
                  </div>

                  <KitMaterials kit={kit} />

                  <div className="flex flex-wrap gap-3">
                    {kit.one_pager_url ? (
                      <a
                        href={kit.one_pager_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2 text-sm font-semibold text-ice/85 hover:border-cyan hover:text-cyan"
                      >
                        <FileText className="h-4 w-4" /> One pager
                      </a>
                    ) : null}
                    {kit.landing_url ? (
                      <a
                        href={kit.landing_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2 text-sm font-semibold text-ice/85 hover:border-cyan hover:text-cyan"
                      >
                        <ExternalLink className="h-4 w-4" /> Program page
                      </a>
                    ) : null}
                  </div>
                </div>
              ) : null}

            </article>
          );
        })}
      </div>
    </AppShell>
  );
}

type KitRecord = {
  email_subject: string;
  email_body: string;
  call_script?: string | null;
  call_tips?: string | null;
  text_message?: string | null;
  text_messages?: unknown;
};

function KitMaterials({ kit }: { kit: KitRecord }) {
  const callScript = kit.call_script?.trim() ?? "";
  const callTips = kit.call_tips?.trim() ?? "";
  const raw = (Array.isArray(kit.text_messages) ? kit.text_messages : []) as {
    label?: string;
    body?: string;
  }[];
  const templates = (
    raw.length
      ? raw
      : kit.text_message?.trim()
        ? [{ label: "Text message", body: kit.text_message }]
        : []
  )
    .map((t, i) => ({ label: t.label?.trim() || `Template ${i + 1}`, body: (t.body ?? "").trim() }))
    .filter((t) => t.body);

  const tabs = [
    { id: "email", label: "Email" },
    ...(callScript || callTips ? [{ id: "call", label: "Call script" }] : []),
    ...(templates.length ? [{ id: "text", label: "Text messages" }] : []),
  ];
  const [tab, setTab] = useState("email");


  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-sm border px-3 py-1.5 text-sm font-semibold ${
              tab === t.id
                ? "border-cyan text-cyan"
                : "border-border text-ice/70 hover:border-cyan hover:text-cyan"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "email" ? (
        <div className="mt-4">
          <p className="t-meta">Email template</p>
          <p className="mt-3 rounded-sm border border-border bg-background px-4 py-3 text-sm font-semibold text-ice">
            {kit.email_subject}
          </p>
          <pre className="mt-2 whitespace-pre-wrap rounded-sm border border-border bg-background px-4 py-3 font-sans text-sm leading-relaxed text-ice/85">
            {kit.email_body}
          </pre>
          <p className="t-meta mt-2">
            Merge fields: contact_first_name, organization, volunteer_name, volunteer_email
          </p>
        </div>
      ) : null}

      {tab === "call" ? (
        <div className="mt-4 space-y-4">
          {callScript ? (
            <div>
              <p className="t-meta">What to say on the call</p>
              <pre className="mt-2 whitespace-pre-wrap rounded-sm border border-border bg-background px-4 py-3 font-sans text-sm leading-relaxed text-ice/85">
                {callScript}
              </pre>
            </div>
          ) : null}
          {callTips ? (
            <div>
              <p className="t-meta">Tips</p>
              <pre className="mt-2 whitespace-pre-wrap rounded-sm border border-border bg-background px-4 py-3 font-sans text-sm leading-relaxed text-ice/85">
                {callTips}
              </pre>
            </div>
          ) : null}
        </div>
      ) : null}

      {tab === "text" ? (
        <div className="mt-4 space-y-4">
          <p className="t-meta">Pick the one that fits the contact, then copy it into your phone.</p>
          {templates.map((t, i) => (
            <div key={i}>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-ice">{t.label}</p>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(t.body);
                    toast.success("Text copied.");
                  }}
                  className="text-sm font-semibold text-cyan"
                >
                  Copy
                </button>
              </div>
              <pre className="mt-2 whitespace-pre-wrap rounded-sm border border-border bg-background px-4 py-3 font-sans text-sm leading-relaxed text-ice/85">
                {t.body}
              </pre>
            </div>
          ))}
        </div>
      ) : null}

    </div>
  );
}
